import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	Container,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";

/**
 * Data quality & completeness (backlog Q-01, CA ANZ gap analysis).
 * Shows, per activity data source and reporting year, whether every facility
 * has all twelve months, how the figure was measured, and exactly what is missing.
 */

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

interface SourceScore {
	key: string;
	label: string;
	scope: number;
	method: "measured" | "calculated" | "estimated";
	facilitiesTotal: number;
	facilitiesCovered: number;
	monthsExpected: number;
	monthsPresent: number;
	score: number;
	status: "complete" | "partial" | "missing";
	missing: { facility: string; months: number[] }[];
}

interface DataQualityResult {
	year: number;
	overall: number;
	facilities: number;
	byMethod: { measured: number; calculated: number; estimated: number };
	sources: SourceScore[];
}

const statusColor = (s: SourceScore["status"]) =>
	s === "complete" ? "success" : s === "partial" ? "warning" : "error";

const methodLabel = (m: SourceScore["method"]) =>
	m === "measured"
		? "Measured (bills, meters)"
		: m === "calculated"
			? "Calculated (activity × factor)"
			: "Estimated (spend-based)";

const DataQuality: React.FC = () => {
	const { getAccessTokenSilently } = useAuth0();
	const thisYear = new Date().getFullYear();
	const [year, setYear] = useState<number>(thisYear - 1);
	const [data, setData] = useState<DataQualityResult | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [nger, setNger] = useState<{
		year: number;
		corporate: { scope12Tonnes: number; thresholdTonnes: number; triggered: boolean };
		facilities: Array<{ facilityID: number; name: string; scope12Tonnes: number; thresholdTonnes: number; triggered: boolean }>;
		safeguard: { thresholdTonnes: number; triggered: boolean };
		reportingObligation: string;
		note: string;
	} | null>(null);

	const load = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			const token = await getAccessTokenSilently();
			const res = await fetch(`${API}/api/data-quality?year=${year}`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			const json = await res.json();
			if (!res.ok || !json.Ok)
				throw new Error(json.Message || "Failed to load data quality");
			setData(json.Data);
			// NGER threshold check (Q-02) — non-fatal if unavailable
			try {
				const ngerRes = await fetch(
					`${API}/api/nger-check?year=${year}`,
					{ headers: { Authorization: `Bearer ${token}` } },
				);
				const ngerJson = await ngerRes.json().catch(() => ({}));
				setNger(ngerRes.ok && ngerJson.Ok ? ngerJson.Data : null);
			} catch {
				setNger(null);
			}
		} catch (e: any) {
			setError(e.message || "Failed to load data quality");
		} finally {
			setLoading(false);
		}
	}, [getAccessTokenSilently, year]);

	useEffect(() => {
		load();
	}, [load]);

	const years = Array.from({ length: 6 }, (_, i) => thisYear - i);

	return (
		<Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
			<Paper elevation={2} sx={{ p: 4 }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 2,
						gap: 2,
						flexWrap: "wrap",
					}}
				>
					<Box>
						<Typography variant="h5">Data quality</Typography>
						<Typography variant="body2" color="text.secondary">
							Completeness of every emissions data source for the
							reporting year, and how each figure was measured. This is
							what an assurance practitioner reviews first.
						</Typography>
					</Box>
					<FormControl size="small" sx={{ minWidth: 140 }}>
						<InputLabel id="dq-year">Reporting year</InputLabel>
						<Select
							labelId="dq-year"
							label="Reporting year"
							value={year}
							onChange={(e) => setYear(Number(e.target.value))}
						>
							{years.map((y) => (
								<MenuItem key={y} value={y}>
									{y}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
						<CircularProgress />
					</Box>
				) : (
					data && (
						<>
							<Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap" }}>
								<Paper variant="outlined" sx={{ p: 2, minWidth: 180 }}>
									<Typography variant="overline">Overall score</Typography>
									<Typography variant="h3" color={data.overall >= 90 ? "success.main" : data.overall >= 50 ? "warning.main" : "error.main"}>
										{data.overall}%
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{data.facilities} facilit{data.facilities === 1 ? "y" : "ies"} · {data.year}
									</Typography>
								</Paper>
								<Paper variant="outlined" sx={{ p: 2, minWidth: 220 }}>
									<Typography variant="overline">Sources with data by method</Typography>
									<Typography variant="body2">Measured: {data.byMethod.measured}</Typography>
									<Typography variant="body2">Calculated: {data.byMethod.calculated}</Typography>
									<Typography variant="body2">Estimated: {data.byMethod.estimated}</Typography>
								</Paper>
							</Box>

							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Source</TableCell>
										<TableCell>Scope</TableCell>
										<TableCell>Method</TableCell>
										<TableCell align="right">Facilities</TableCell>
										<TableCell align="right">Facility-months</TableCell>
										<TableCell align="right">Score</TableCell>
										<TableCell>Missing</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{data.sources.map((s) => (
										<TableRow key={s.key}>
											<TableCell>{s.label}</TableCell>
											<TableCell>{s.scope}</TableCell>
											<TableCell>{methodLabel(s.method)}</TableCell>
											<TableCell align="right">
												{s.facilitiesCovered}/{s.facilitiesTotal}
											</TableCell>
											<TableCell align="right">
												{s.monthsPresent}/{s.monthsExpected}
											</TableCell>
											<TableCell align="right">
												<Chip size="small" color={statusColor(s.status)} label={`${s.score}%`} />
											</TableCell>
											<TableCell sx={{ maxWidth: 360 }}>
												{s.missing.length === 0 ? (
													<Typography variant="caption" color="text.secondary">None</Typography>
												) : (
													s.missing.map((m) => (
														<Typography key={m.facility} variant="caption" display="block">
															<strong>{m.facility}</strong>
															{m.months.length > 0 && m.months.length < 12 && `: ${m.months.map((n) => MONTHS[n - 1]).join(", ")}`}
															{m.months.length === 12 && ": no data for the year"}
														</Typography>
													))
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</>
					)
				)}
			</Paper>

			{nger && (
				<Paper elevation={2} sx={{ p: 4, mt: 3 }}>
					<Typography variant="h6" gutterBottom>
						NGER threshold check ({nger.year})
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Reporting under the National Greenhouse and Energy
						Reporting scheme is{" "}
						<strong>{nger.reportingObligation}</strong> on these
						figures. {nger.note}
					</Typography>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Level</TableCell>
								<TableCell align="right">Scope 1+2 t CO₂e</TableCell>
								<TableCell align="right">Threshold</TableCell>
								<TableCell align="right">Status</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							<TableRow>
								<TableCell><strong>Corporate group</strong></TableCell>
								<TableCell align="right">{nger.corporate.scope12Tonnes.toLocaleString()}</TableCell>
								<TableCell align="right">{nger.corporate.thresholdTonnes.toLocaleString()} t</TableCell>
								<TableCell align="right">
									<Chip size="small" color={nger.corporate.triggered ? "error" : "success"} label={nger.corporate.triggered ? "Threshold met" : "Below threshold"} />
								</TableCell>
							</TableRow>
							{nger.facilities.map((f) => (
								<TableRow key={f.facilityID}>
									<TableCell>{f.name}</TableCell>
									<TableCell align="right">{f.scope12Tonnes.toLocaleString()}</TableCell>
									<TableCell align="right">{f.thresholdTonnes.toLocaleString()} t</TableCell>
									<TableCell align="right">
										<Chip size="small" color={f.triggered ? "error" : "success"} label={f.triggered ? "Threshold met" : "Below threshold"} />
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Paper>
			)}
		</Container>
	);
};

export default DataQuality;
